import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Button, FormControl, Input, InputLabel, Typography, Link} from '@material-ui/core';
import InputMask from 'react-input-mask';
import {ReactComponent as Success} from '../assets/green-confirm-check.svg';
import parsePhoneNumber from 'libphonenumber-js'
import {ReactComponent as AddContact} from '../assets/add-contact.svg';
import { __ } from '../lib/localization';

const ContactForm = (props) => {
    const [isProcessingForm, setIsProcessingForm] = useState(false);
    const [isFormSent, setIsFormSent] = useState(false);
    const [errors, setErrors] = useState({});
    const [phone, setPhone] = useState("");
    const [name, setName] = useState("");
    const [message, setMessage] = useState("");
    const formWrapperRef = useRef(null);
    const [isFormStarted, setIsFormStarted] = useState(false);

    const getPhoneNumber = () => {
        return parsePhoneNumber(props.callFromNumber ?? '+12133734253')?.formatNational(); // Failsafe just in case no cellphone is returned
    }

    const getAddToContactsBlob = () => {
        const vcardData = ['BEGIN:VCARD', 'VERSION:3.0'];

        if (props.vcardData.org) {
            vcardData.push('ORG:' + props.vcardData.org);
        }
        if (props.vcardData.message_number) {
            vcardData.push('TEL;TYPE=MSG:' + props.vcardData.message_number);
        }
        if (props.vcardData.voice_number) {
            vcardData.push('TEL;TYPE=VOICE:' + props.vcardData.voice_number);
        }
        if (props.vcardData.url) {
            vcardData.push('URL:' + props.vcardData.url);
        }
        if (props.vcardData.adr) {
            vcardData.push('ADR:' + props.vcardData.adr);
        }
        if (props.vcardData.logo) {
            let logo = props.vcardData.logo;
            const type = logo.startsWith('data:image/png')
                ? 'PNG'
                : 'JPEG';
            vcardData.push('PHOTO;ENCODING=b;TYPE='+type+':' + props.vcardData.logo.replace(/^.*base64,/, ''));
        }

        vcardData.push('END:VCARD');

        var file = new Blob([vcardData.join("\n")], {type: 'text/x-vcard'});

        return URL.createObjectURL(file);
    }

    const getAddToContactsFilename = () => {
        let name = 'vcard';

        if (props.vcardData.org) {
            name = (props.vcardData.org)
                .toLowerCase()
                .replace(/\W/, '-')
                .replace(/[-]+/, '-')
                .replace(/(^[-]+|[-]+$)/, '')
        }

        return name + '.vcf';
    }

    const onFieldFocus = (inputName) => {
        props.Analytics.trackEvent('form-field', inputName)
        if (!isFormStarted) {
            props.Analytics.trackEvent('form-field', 'form_start')
            setIsFormStarted(true);
        }
    }

    const validate = () => {
        setIsProcessingForm(true);
        props.Analytics.trackEvent('buttons', 'submit-cta');

        const isPhone = RegExp(/^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/) // us

        let error = {}
        let valid = true;
        if (phone === "") {
            error.phone = __('leadbox_required');
            props.Analytics.trackEvent('form-field-error', 'phone');
            valid = false;
        } else {
            const cleanPhone = phone.replace("+1", "").replace(" ", "");
            const isValid = isPhone.test(cleanPhone);
            if (!isValid) {
                error.phone = __('leadbox_error_number');
                props.Analytics.trackEvent('form-field-error', 'phone');
                valid = false
            }
        }
        if (name === "") {
            error.name = __('leadbox_required');
            valid = false;
        } else if (name.length < 3) {
            error.name = __('leadbox_error_name');
            props.Analytics.trackEvent('form-field-error', 'name');
            valid = false;
        }

        if (valid) {
            const cleanPhone = phone
                .replace(/\-/g, "")
                .replace(/\s/g, "")
                .replace("(", "")
                .replace(")", "");
            const obj = JSON.stringify({
                name: name,
                cellphone: cleanPhone,
                message: message.substring(0, 3000),
                source: props.Analytics.getHref()
            });
            axios.post(props.endpoint, obj, {
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    }
                }
            ).then((e) => {
                if (e.status === 204) {
                    setErrors({});
                    props.Analytics.trackEvent('leadbox', 'form-submitted');
                    setTimeout(() => {
                        formWrapperRef?.current?.focus(); // we focus the content wrapper to set the tab position
                        formWrapperRef?.current?.blur(); // and then we blur so as to not show the :focus styling around the div. The tab position should still be where we want it.
                    }, 125);
                    return setIsFormSent(true)
                }
            }).catch((err) => {
                switch (err.response.data.errors[0].errorCode) {
                    case 'invalidPhoneNumber':
                        error.phone = __('leadbox_error_number');
                        break;
                    case 'maxLength.openapi.validation':
                        error.name = __('leadbox_error_msg');
                        break;
                    default:
                        error.api = err.response.data.message;

                }
                valid = false;
                return setErrors(error);
            })
            .finally(e => setIsProcessingForm(false));
        } else {
            setIsProcessingForm(false);
            return setErrors(error);
        }
    };

    return (
        <div ref={formWrapperRef} tabindex="-1">
            {!isFormSent ? (
                <form id="leadbox-contact-form" name="leadbox-contact-form" autoComplete="off" className={props.classes.form__body}>
                    <FormControl fullWidth>
                        <InputLabel
                            className={props.classes.root}
                            htmlFor="name"
                            error={errors.name !== undefined}
                        >
                            {__('leadbox_name')} <span>({errors.name})</span>
                        </InputLabel>
                        <Input
                            className={props.classes.root}
                            required
                            id="name"
                            value={name.slice(0,69)}
                            onFocus={() => { onFieldFocus('name') }}
                            onChange={(e) => { setName(e.target.value) }}
                        />
                    </FormControl>

                    <div>
                        <InputMask
                            mask="+1 (999) 999-9999"
                            value={phone}
                            disabled={false}
                            maskChar=" "
                            onChange={(e) => {
                                (errors.phone !== undefined) && setErrors(prevState => ({
                                    ...prevState,
                                    phone: undefined
                                }));
                                setPhone(e.target.value)
                            }}
                        >
                            {() => <FormControl fullWidth>
                                <InputLabel
                                    className={props.classes.root} htmlFor="phone"
                                    error={errors.phone !== undefined}
                                >
                                    {__('leadbox_number')}
                                    <span>({errors.phone})</span>
                                </InputLabel>
                                <Input
                                    className={props.classes.root}
                                    required
                                    id="phone"
                                    onFocus={() => { onFieldFocus('phone') }}
                                />
                            </FormControl>}
                        </InputMask>
                    </div>

                    <div>
                        <FormControl fullWidth>
                            <InputLabel className={props.classes.root} htmlFor="message">{__('leadbox_message')}</InputLabel>
                            <Input
                                multiline
                                rows={2}
                                className={props.classes.textArea}
                                required
                                id="message"
                                value={message}
                                onFocus={() => { onFieldFocus('message') }}
                                onChange={(e) => {
                                    setMessage(e.target.value)
                            }}/>
                        </FormControl>
                    </div>
                </form>
            ) : (
                <div className={props.classes.form__body}>
                    {!props.vcardData?.org ? (
                        <Success style={{marginTop: '15px'}} aria-hidden="true" focusable="false" />
                     ) : (<></>)}
                    <Typography className={props.classes.success__text} style={{marginTop: (props.vcardData?.org ? '15px' : '')}}><strong style={{textTransform: 'uppercase'}}>{ __('leadbox_thank_you') }</strong><br/><br/>
                        {props.vcardData?.org ? (
                         <React.Fragment>
                             <Button
                                 href={getAddToContactsBlob()}
                                 classes={{
                                     root: props.classes.addToContactsButton,
                                     label: props.classes.addToContactsButton__label,
                                 }}
                                 style={{backgroundColor: "#39AF25", margin: "5px auto"}}
                                 onClick={ () => { props.Analytics.trackEvent('buttons', 'add-to-contacts-cta') }}
                                 download={getAddToContactsFilename()}
                                 startIcon=<AddContact aria-hidden="true" focusable="false" />
                             >
                                 { __('leadbox_cta_vcard') }
                             </Button>
                             <br/><br/>
                         </React.Fragment>
                         ) : (<></>)}
                        { __('leadbox_confirm_msg') } <br/><span data-calltrk-noswap><strong>{getPhoneNumber()}</strong></span>
                    </Typography>
                </div>
            )}

            <div className={props.classes.form__triangle}></div>
            <div>
                 {!isFormSent ? (
                 <div>
                    <Typography className={props.classes.submit__note}>
                     {__(
                         'leadbox_submit_terms',
                         { Business_name: props.vcardData?.org || "" }
                      )}
                    </Typography>
                    <Typography className={props.classes.error}>{errors?.api}</Typography>
                 </div>
                 ) : (<></>)}
                 <Button
                     classes={{
                         root: props.classes.sendbutton,
                         label: props.classes.sendbutton__label,
                         disabled: props.classes.sendbutton__disabled
                     }}
                     disabled={isProcessingForm || name === "" || phone == ""}
                     style={{...{backgroundColor: props.accentColor}, ...(isFormSent ? {marginTop: "28px"} : {}), ...(isFormSent && props.vcardData?.org ? {marginBottom: "72px"} : {})}}
                     onClick={isFormSent ? props.handleCloseLeadbox : validate}
                     aria-label={isFormSent ? __('leadbox_close') : __('leadbox_send')}
                 >{isFormSent ? __('leadbox_close') : __('leadbox_send')}</Button>
            </div>
        </div>
    )
}

export default ContactForm;
